import React, { useEffect, useRef, useState } from "react";

// Backend base (no trailing slash)
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "https://onetapcast-signal.onrender.com").replace(/\/+$/, "");
const WS_BASE = BACKEND.replace(/^http/i, "ws");

// LAN-only mode: set REACT_APP_LOCAL_ONLY=true in Vercel
const LOCAL_ONLY = (process.env.REACT_APP_LOCAL_ONLY === "true");

// TURN config via env (ignored when LOCAL_ONLY=true)
function parseTurnUrls(v) {
  if (!v) return [];
  return v.split(",").map(s => s.trim()).filter(Boolean);
}
const TURN_URLS = parseTurnUrls(process.env.REACT_APP_TURN_URLS || "");
const TURN_USERNAME = process.env.REACT_APP_TURN_USERNAME || "";
const TURN_CREDENTIAL = process.env.REACT_APP_TURN_CREDENTIAL || "";

// ICE config for LAN mode
const ICE_SERVERS = LOCAL_ONLY
  ? [] // Empty for LAN-only
  : [
      { urls: "stun:stun.l.google.com:19302" },
      ...(TURN_URLS.length ? [{
        urls: TURN_URLS,
        username: TURN_USERNAME,
        credential: TURN_CREDENTIAL
      }] : [])
    ];

const RTC_CONFIG = {
  iceServers: ICE_SERVERS,
  iceTransportPolicy: "all",
  bundlePolicy: "balanced",
  rtcpMuxPolicy: "require"
};

// Updated LAN candidate filtering with .local hostname support
function isValidLANCandidate(candidate) {
  if (!LOCAL_ONLY) return true;
  
  const candidateStr = (candidate && candidate.candidate) || "";
  console.log("🔍 LAN Mode: Analyzing candidate:", candidateStr);
  
  // Allow host candidates (direct connections)
  if (candidateStr.includes("typ host")) {
    // Check for .local mDNS hostnames (legitimate local network addresses)
    if (candidateStr.includes(".local")) {
      console.log("✅ LAN Mode: Host candidate with .local hostname - ALLOWED");
      return true;
    }
    
    // Extract IP address for numeric IPs
    const ipMatch = candidateStr.match(/(\d+\.\d+\.\d+\.\d+)/);
    if (!ipMatch) {
      // No numeric IP found but it's a host candidate - likely legitimate local
      console.log("✅ LAN Mode: Host candidate without numeric IP (likely local) - ALLOWED");
      return true;
    }
    
    const ip = ipMatch[1];
    const parts = ip.split('.').map(Number);
    
    // Check for private/local IP ranges
    const isLocal = 
      (parts[0] === 192 && parts[1] === 168) ||           // 192.168.x.x
      (parts[0] === 10) ||                                 // 10.x.x.x
      (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) || // 172.16-31.x.x
      (parts[0] === 127) ||                                // 127.x.x.x (localhost)
      (parts[0] === 169 && parts[1] === 254) ||           // 169.254.x.x (link-local)
      ip === "0.0.0.0";                                   // Any interface
      
    console.log(`${isLocal ? '✅' : '❌'} LAN Mode: Host candidate ${ip} - ${isLocal ? 'ALLOWED' : 'BLOCKED'}`);
    return isLocal;
  }
  
  // Allow srflx (server reflexive) only from local IP ranges
  if (candidateStr.includes("typ srflx")) {
    const ipMatch = candidateStr.match(/(\d+\.\d+\.\d+\.\d+)/);
    if (ipMatch) {
      const ip = ipMatch[1];
      const parts = ip.split('.').map(Number);
      
      const isLocalRange = 
        (parts[0] === 192 && parts[1] === 168) ||
        (parts[0] === 10) ||
        (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31);
        
      console.log(`${isLocalRange ? '⚠️' : '❌'} LAN Mode: SRFLX candidate ${ip} - ${isLocalRange ? 'ALLOWED (local range)' : 'BLOCKED (public)'}`);
      return isLocalRange;
    }
  }
  
  // Block relay candidates (TURN server routes)
  if (candidateStr.includes("typ relay")) {
    console.log("❌ LAN Mode: BLOCKED relay candidate (TURN server)");
    return false;
  }
  
  console.log("❌ LAN Mode: BLOCKED unknown candidate type");
  return false;
}

// Connection validation with detailed logging
function validateLANConnection(pc) {
  if (!LOCAL_ONLY) return;
  
  console.log("🔗 LAN Mode: Setting up connection validation");
  
  pc.addEventListener('iceconnectionstatechange', () => {
    console.log('🔗 LAN Mode: ICE connection state:', pc.iceConnectionState);
    
    if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
      console.log('✅ LAN Mode: Connection established successfully');
      
      // Check connection stats
      pc.getStats().then(stats => {
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            console.log('🎯 LAN Mode: Active connection pair:', report);
          }
          if (report.type === 'local-candidate' && report.candidateType) {
            console.log('📍 LAN Mode: Local candidate used:', report);
          }
          if (report.type === 'remote-candidate' && report.candidateType) {
            console.log('📍 LAN Mode: Remote candidate used:', report);
          }
        });
      });
    }
    
    if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
      console.log('❌ LAN Mode: Connection failed/disconnected');
    }
  });
  
  pc.addEventListener('icegatheringstatechange', () => {
    console.log('🔗 LAN Mode: ICE gathering state:', pc.iceGatheringState);
  });
  
  // Log all ICE candidates for debugging
  pc.addEventListener('icecandidate', (event) => {
    if (event.candidate) {
      console.log('🧊 LAN Mode: Generated ICE candidate:', event.candidate.candidate);
    }
  });
}

// Helpers
function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

async function waitBackendReady(timeoutMs = 30000) {
  const end = Date.now() + timeoutMs;
  while (Date.now() < end) {
    try {
      const r = await fetch(`${BACKEND}/api/health`, { cache: "no-store" });
      if (r.ok) return true;
    } catch (_) {}
    await sleep(1200);
  }
  return false;
}

function newId(len = 10) {
  const s = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: len }, () => s[Math.floor(Math.random() * s.length)]).join("");
}

function Button({ children, className = "", ...props }) {
  return <button className={"btn " + className} {...props}>{children}</button>;
}

function Badge({ children, className = "" }) {
  return <span className={`badge ${className}`}>{children}</span>;
}

// Enhanced LAN Mode Badge Component
const LANBadge = () => LOCAL_ONLY ? (
  <Badge className="lan-badge">
    🔗 LAN MODE
  </Badge>
) : null;

// Icons
const IconCast = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ marginRight: 6 }}>
    <path d="M2 16a6 6 0 0 1 6 6H6a4 4 0 0 0-4-4v-2Z" fill="#19B394" />
    <path d="M2 12a10 10 0 0 1 10 10h-2C10 18.477 7.523 16 4 16H2v-4Z" fill="#19B394" opacity=".6" />
    <path d="M21 6H7a1 1 0 0 0-1 1v1H4V7a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3h-6v-2h6a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z" fill="#111827" />
  </svg>
);

const IconRouter = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2" y="3" width="20" height="14" rx="2" stroke="#19B394" strokeWidth="2" fill="none"/>
    <circle cx="8" cy="8" r="1" fill="#19B394"/>
    <circle cx="12" cy="8" r="1" fill="#19B394"/>
    <path d="M6 13h12" stroke="#19B394" strokeWidth="2"/>
    <path d="M9 17h6" stroke="#19B394" strokeWidth="2"/>
  </svg>
);

const IconArrowDownCircle = ({ size = 22, color = "#2F81F7" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} opacity=".12" />
    <path d="M12 6v9m0 0l4-4m-4 4l-4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconArrowUpCircle = ({ size = 22, color = "#19B394" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="11" fill={color} opacity=".12" />
    <path d="M12 18V9m0 0l4 4m-4-4l-4 4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconMonitor = ({ size = 54 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="12" rx="2" stroke="#94a3b8" strokeWidth="1.5" />
    <path d="M8 20h8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 16v4M14 16v4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function App() {
  const [page, setPage] = useState("home");
  const [status, setStatus] = useState("Disconnected");
  const [rxFit, setRxFit] = useState("contain");

  // Enhanced logging on startup
  useEffect(() => {
    console.log('🚀 OneTapCast LAN Mode Status:', LOCAL_ONLY ? 'ENABLED' : 'DISABLED');
    console.log('🚀 ICE Servers:', ICE_SERVERS);
    console.log('🚀 RTC Config:', RTC_CONFIG);
    console.log('🚀 Backend URL:', BACKEND);
    if (LOCAL_ONLY) {
      console.log('🔒 LAN Mode: Enhanced debugging active. Check console for detailed connection logs.');
    }
  }, []);

  useEffect(() => {
    const setVh = () => document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);

  useEffect(() => {
    fetch(`${BACKEND}/api/health`, { cache: "no-store" }).catch(() => {});
  }, []);

  // ADD THIS KEEP-ALIVE CODE HERE:
  useEffect(() => {
    console.log('🔄 Starting keep-alive to help prevent backend sleeping');
    const keepAlive = setInterval(() => {
      fetch(`${BACKEND}/api/health`, { cache: "no-store" }).catch(() => {
        console.log('⚠️ Keep-alive ping failed - backend may be sleeping');
      });
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => {
      console.log('🔄 Stopping keep-alive');
      clearInterval(keepAlive);
    };
  }, []);
  // END of KEEP-ALIVE CODE
  
  useEffect(() => {
    const onFs = () => {
      const active = !!document.fullscreenElement;
      document.body.classList.toggle("is-fullscreen", active);
      if (!active) document.body.classList.remove("immersive");
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  function enterFullscreenOrImmersive(paneId) {
    const el = document.getElementById(paneId) || document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => document.body.classList.add("immersive"));
    } else {
      document.body.classList.add("immersive");
    }
  }

  function exitFullscreen() {
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen();
      } catch {}
    }
    document.body.classList.remove("immersive");
    document.body.classList.remove("is-fullscreen");
  }

  useEffect(() => {
    document.body.classList.toggle("receiver-mode", page === "receiver");
    if (page !== "receiver") exitFullscreen();
  }, [page]);

  const [rxCode, setRxCode] = useState("");
  const [connectedCount, setConnectedCount] = useState(0);
  const [rxMuted, setRxMuted] = useState(true);
  const [rxHasStream, setRxHasStream] = useState(false);
  const rxWsRef = useRef(null);
  const rxPcRef = useRef(null);
  const rxIdRef = useRef(newId());
  const rxVideoRef = useRef(null);
  const rxRunningRef = useRef(false);

  const [txCode, setTxCode] = useState("");
  const [includeAudio, setIncludeAudio] = useState(true);
  const txWsRef = useRef(null);
  const txPcRef = useRef(null);
  const txIdRef = useRef(newId());
  const txVideoRef = useRef(null);
  const txStreamRef = useRef(null);
  const [senderConnected, setSenderConnected] = useState(false);

  useEffect(() => {
    if (page === "receiver" && !rxRunningRef.current) startReceiver();
  }, [page]);

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  }

  const getStatusMessage = (baseStatus) => {
    if (LOCAL_ONLY) {
      return "🔗 LAN Mode: " + baseStatus;
    }
    return baseStatus;
  };

  async function startReceiver() {
    if (rxRunningRef.current) return;
    rxRunningRef.current = true;

    console.log('🎯 Starting receiver in LAN mode');
    setStatus(getStatusMessage("Waking backend..."));
    const ready = await waitBackendReady(30000);
    if (!ready) {
      rxRunningRef.current = false;
      setStatus(getStatusMessage("Disconnected"));
      alert("Server waking. Try again.");
      return;
    }

    setStatus(getStatusMessage("Creating room..."));
    let code = "";
    try {
      const r = await fetch(`${BACKEND}/api/session/create`, { method: "POST" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      code = data.room_code;
      setRxCode(code);
      console.log('🎯 Room created with code:', code);
    } catch (e) {
      rxRunningRef.current = false;
      setStatus(getStatusMessage("Disconnected"));
      alert("Could not create room. Try again.");
      return;
    }

    setStatus(getStatusMessage("Waiting for sender..."));
    const ws = new WebSocket(`${WS_BASE}/api/ws/${rxIdRef.current}`);
    rxWsRef.current = ws;

    ws.onopen = () => {
      console.log('🔗 Receiver WebSocket connected');
      ws.send(JSON.stringify({ type: "join", room_code: code, role: "receiver" }));
    };

    ws.onmessage = async (ev) => {
      const msg = JSON.parse(ev.data);
      console.log('📨 Receiver received message:', msg.type);

      if (msg.type === "sender_joined") {
        console.log('👤 Sender joined');
        setConnectedCount(c => c + 1);
      }
      if (msg.type === "sender_left") setConnectedCount(c => Math.max(0, c - 1));

      if (msg.type === "offer") {
        console.log('📞 Received offer, starting connection...');
        await handleReceiverOffer(msg);
      }

      if (msg.type === "candidate" && rxPcRef.current) {
        if (!isValidLANCandidate(msg.candidate)) {
          return;
        }
        try {
          await rxPcRef.current.addIceCandidate(msg.candidate);
          console.log('✅ Successfully added ICE candidate');
        } catch (e) {
          console.error('❌ Failed to add ICE candidate:', e);
        }
      }

      if (["sharing_stopped", "bye", "sender_left"].includes(msg.type)) {
        console.log('🛑 Connection ended:', msg.type);
        exitFullscreen();
        clearReceiverVideo();
        setStatus(getStatusMessage("Waiting for sender..."));
      }
    };

    ws.onclose = () => {
      console.log('🔗 Receiver WebSocket closed');
      setStatus(getStatusMessage("Disconnected"));
      rxRunningRef.current = false;
      setConnectedCount(0);
      setRxHasStream(false);
    };
    ws.onerror = (e) => {
      console.error('❌ Receiver WebSocket error:', e);
      setStatus(getStatusMessage("Receiver WS error"));
    };
  }

  async function handleReceiverOffer(msg) {
    console.log('🎯 Creating peer connection for receiver');
    setStatus(getStatusMessage("Connecting..."));
    const pc = new RTCPeerConnection(RTC_CONFIG);
    rxPcRef.current = pc;
    
    validateLANConnection(pc);

    pc.onicecandidate = (e) => {
      if (!e.candidate) {
        console.log('🧊 ICE gathering complete');
        return;
      }
      
      if (!isValidLANCandidate(e.candidate)) {
        return;
      }
      
      console.log('📤 Sending ICE candidate to sender');
      if (rxWsRef.current) {
        rxWsRef.current.send(JSON.stringify({
          type: "candidate",
          candidate: e.candidate,
          sender_id: msg.sender_id
        }));
      }
    };

    pc.ontrack = (e) => {
      console.log('🎥 Received media track:', e.track.kind);
      const v = rxVideoRef.current;
      if (v && e.streams && e.streams[0]) {
        v.srcObject = e.streams[0];
        //v.muted = rxMuted; --old code
        //v.volume = 1.0; --old code
        //setRxHasStream(true);--old code
        // Start muted to comply with autoplay restrictions
        v.muted = true;
        setRxMuted(true);
        v.volume = 1.0;
        setRxHasStream(true);
        // end of new code addition
        console.log('✅ Video stream set successfully');
       // Attempt to auto-unmute after 500ms, which may help simulate user interaction
      setTimeout(() => {
      if (e.streams[0].getAudioTracks().length > 0) {
        console.log('🔊 Attempting to auto-unmute audio stream');
        v.muted = false;
        setRxMuted(false);
        v.play().catch(() => {
          console.log('Audio still requires user interaction to play');
        });
        }
      }, 500);
        // End of new code
        const p = v.play();
        if (p && p.catch) p.catch((err) => console.log('Video play error:', err));
      }
    };

    try {
      await pc.setRemoteDescription(msg.offer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      rxWsRef.current?.send(JSON.stringify({
        type: "answer",
        answer,
        sender_id: msg.sender_id
      }));
      console.log('📤 Answer sent to sender');
      setStatus(getStatusMessage("Connected"));
    } catch (e) {
      console.error('❌ Error in handleReceiverOffer:', e);
    }
  }

  function clearReceiverVideo() {
    try {
      const v = rxVideoRef.current;
      if (v?.srcObject) {
        (v.srcObject.getTracks?.() || []).forEach(t => t.stop?.());
        v.srcObject = null;
      }
    } catch {}
    setRxHasStream(false);
  }

  async function startSender() {
    const code = (txCode || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      alert("Enter 6-digit code");
      return;
    }

    console.log('🎯 Starting sender in LAN mode with code:', code);
    setStatus(getStatusMessage("Waking backend..."));
    const ready = await waitBackendReady(30000);
    if (!ready) {
      setStatus(getStatusMessage("Disconnected"));
      alert("Server waking. Try again.");
      return;
    }

    try {
      const r = await fetch(`${BACKEND}/api/session/${code}`);
      const data = await r.json();
      if (!data.exists) {
        alert("Room does not exist");
        setStatus(getStatusMessage("Disconnected"));
        return;
      }
    } catch (e) {
      alert("Failed to reach backend");
      setStatus(getStatusMessage("Disconnected"));
      return;
    }

    setStatus(getStatusMessage("Requesting screen..."));
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: includeAudio
      });
      console.log('🎥 Screen capture successful');
    } catch (e) {
      console.error('❌ Screen capture failed:', e);
      alert("Screen capture blocked or unsupported");
      setStatus(getStatusMessage("Disconnected"));
      return;
    }
    
    txStreamRef.current = stream;
    if (txVideoRef.current) {
      txVideoRef.current.srcObject = stream;
      txVideoRef.current.muted = true;
      const p = txVideoRef.current.play();
      if (p && p.catch) p.catch(() => {});
    }

    console.log('🎯 Creating peer connection for sender');
    setStatus(getStatusMessage("Connecting..."));
    const pc = new RTCPeerConnection(RTC_CONFIG);
    txPcRef.current = pc;
    
    validateLANConnection(pc);
    
    stream.getTracks().forEach(t => {
      pc.addTrack(t, stream);
      console.log('➕ Added track to peer connection:', t.kind);
    });

    const ws = new WebSocket(`${WS_BASE}/api/ws/${txIdRef.current}`);
    txWsRef.current = ws;

    pc.onicecandidate = (e) => {
      if (!e.candidate) {
        console.log('🧊 ICE gathering complete');
        return;
      }
      
      if (!isValidLANCandidate(e.candidate)) {
        return;
      }
      
      console.log('📤 Sending ICE candidate to receiver');
      if (ws.readyState === 1) {
        ws.send(JSON.stringify({
          type: "candidate",
          candidate: e.candidate
        }));
      }
    };

    ws.onopen = async () => {
      console.log('🔗 Sender WebSocket connected');
      ws.send(JSON.stringify({
        type: "join",
        room_code: code,
        role: "sender"
      }));
      
      console.log('📞 Creating and sending offer...');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      await pc.setLocalDescription(offer);
      ws.send(JSON.stringify({
        type: "offer",
        offer
      }));
      setStatus(getStatusMessage("Offer sent"));
    };

    ws.onmessage = async (ev) => {
      const msg = JSON.parse(ev.data);
      console.log('📨 Sender received message:', msg.type);
      
      if (msg.type === "answer") {
        console.log('📞 Received answer, completing connection...');
        await pc.setRemoteDescription(msg.answer);
        setStatus(getStatusMessage("Connected"));
        setSenderConnected(true);
      }
      if (msg.type === "candidate") {
        if (!isValidLANCandidate(msg.candidate)) {
          return;
        }
        try {
          await pc.addIceCandidate(msg.candidate);
          console.log('✅ Successfully added ICE candidate');
        } catch (e) {
          console.error('❌ Failed to add ICE candidate:', e);
        }
      }
      if (msg.type === "receiver_left" || msg.type === "bye") {
        console.log('🛑 Receiver left');
        stopSender();
        setStatus(getStatusMessage("Receiver left"));
      }
    };

    ws.onerror = (e) => {
      console.error('❌ Sender WebSocket error:', e);
      setStatus(getStatusMessage("Sender WS error"));
    };
    ws.onclose = () => {
      console.log('🔗 Sender WebSocket closed');
      setStatus(getStatusMessage("Sender disconnected"));
    };

    const vt = stream.getVideoTracks()[0];
    if (vt) vt.addEventListener("ended", () => stopSender());
  }

  function stopSender() {
    console.log('🛑 Stopping sender');
    try {
      txWsRef.current?.send(JSON.stringify({ type: "sharing_stopped" }));
    } catch {}
    try {
      txWsRef.current?.send(JSON.stringify({ type: "bye" }));
    } catch {}
    try {
      txWsRef.current?.close();
    } catch {}
    txWsRef.current = null;

    try {
      txPcRef.current?.getSenders().forEach(s => s.track && s.track.stop());
    } catch {}
    try {
      txPcRef.current?.close();
    } catch {}
    txPcRef.current = null;

    try {
      txStreamRef.current?.getTracks().forEach(t => t.stop());
    } catch {}
    txStreamRef.current = null;

    setSenderConnected(false);
    setStatus(getStatusMessage("Disconnected"));
  }

  function disconnectSender() {
    stopSender();
    setTxCode("");
  }

  const CodeDots = ({ code }) => {
    const chars = (code || "").padEnd(6, " ").slice(0, 6).split("");
    return (
      <div className="code-dots">
        {chars.map((ch, i) => (
          <div className="dot" key={i}>
            {/^[A-Z0-9]$/.test(ch) ? ch : " "}
          </div>
        ))}
      </div>
    );
  };

  const TopBar = (
    <div className="topbar">
      {page !== "home" ? (
        <button
          className="link-back"
          onClick={() => {
            setPage("home");
            setStatus("Disconnected");
          }}
        >
          ← Back to Home
        </button>
      ) : (
        <span />
      )}
      <div className="status-right">
        <LANBadge />
        <span className="status-dot" />
        {status}
      </div>
    </div>
  );

  return (
    <div className="ui-wrap">
      {TopBar}

      {page === "home" && (
        <div className="landing">
          {LOCAL_ONLY && (
            <div className="lan-notice">
              <IconRouter size={24} />
              <div>
                <strong>🔒 LAN-Only Mode Active</strong>
                <p>Enhanced local network filtering active. Both devices must be on the same Wi-Fi or ethernet network.</p>
              </div>
            </div>
          )}
          
          <h1 className="brand-hero">
            <IconCast /> OneTapCast
            {LOCAL_ONLY && <Badge className="lan-badge-hero">🔒 LAN</Badge>}
          </h1>
          <p className="tagline">
            Share your screen seamlessly across all devices.{" "}
            {LOCAL_ONLY
              ? "🔒 Secure LAN-only mode — works entirely on your local network."
              : "Cast from any browser — no apps required."}
          </p>

          <div className="cards-2">
            <div
              className="card-cta"
              onClick={() => setPage("receiver")}
              role="button"
              tabIndex={0}
            >
              <div className="cta-icon">
                <IconArrowDownCircle />
              </div>
              <div className="cta-title">View Screen</div>
              <ul className="cta-list">
                <li>Get a pairing code</li>
                <li>View shared screens</li>
                {LOCAL_ONLY && <li>🔒 LAN-only connection</li>}
              </ul>
            </div>

            <div
              className="card-cta"
              onClick={() => setPage("sender")}
              role="button"
              tabIndex={0}
            >
              <div className="cta-icon green">
                <IconArrowUpCircle />
              </div>
              <div className="cta-title">Share Screen</div>
              <ul className="cta-list">
                <li>Enter pairing code</li>
                <li>Start screen sharing</li>
                {LOCAL_ONLY && <li>🔒 Direct network streaming</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {page === "sender" && (
        <div className="sender-wrap">
          <div className="card wide">
            <div className="card-title">
              <IconArrowUpCircle /> Sharing Mode {LOCAL_ONLY && <LANBadge />}
            </div>
            <div className="card-sub">
              {LOCAL_ONLY
                ? "🔒 LAN Mode: Both devices must be on the same Wi‑Fi/network."
                : "Enter the room code from the receiving device."}
            </div>
            <div className="sender-row">
              <input
                className="input large"
                placeholder="Enter 6-digit code"
                value={txCode}
                onChange={e => setTxCode(e.target.value.toUpperCase())}
              />
              {!senderConnected ? (
                <Button className="secondary" onClick={startSender}>
                  Connect
                </Button>
              ) : (
                <>
                  <Button className="secondary" onClick={stopSender}>
                    Stop Sharing
                  </Button>
                  <Button className="ghost" onClick={disconnectSender}>
                    Disconnect
                  </Button>
                </>
              )}
            </div>
            <div className="hint">
              {LOCAL_ONLY
                ? "🔒 LAN Mode: Only local network connections allowed. Cross-network access blocked."
                : "Tip: Share a Chrome tab and check 'Share tab audio' for audio."}
            </div>
          </div>
          <video
            ref={txVideoRef}
            autoPlay
            playsInline
            muted
            style={{ display: "none" }}
          />
        </div>
      )}

      {page === "receiver" && (
        <div className="receiver-grid">
          <div className="left">
            <div className="card">
              <div className="card-title">
                <IconArrowDownCircle /> Viewing Mode {LOCAL_ONLY && <LANBadge />}
              </div>
              <div className="card-sub">
                {LOCAL_ONLY 
                  ? "🔒 Share this code with devices on your local network only"
                  : "Share this code with devices you want to cast from"
                }
              </div>
              <CodeDots code={rxCode} />
              <div className="row">
                <Button
                  className="ghost"
                  onClick={() => navigator.clipboard.writeText(rxCode)}
                  disabled={!rxCode}
                >
                  Copy Code
                </Button>
                <Button
                  className="ghost"
                  onClick={() =>
                    setRxFit(rxFit === "contain" ? "cover" : "contain")
                  }
                >
                  Fit: {rxFit === "contain" ? "Contain" : "Cover"}
                </Button>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={rxMuted}
                    onChange={e => {
                      setRxMuted(e.target.checked);
                      try {
                        const v = rxVideoRef.current;
                        if (v) {
                          v.muted = e.target.checked;
                          if (!e.target.checked) v.play().catch(() => {});
                        }
                      } catch {}
                    }}
                  />
                  {" "}Mute
                </label>
              </div>
              <div className="muted">
                Connected Devices: {connectedCount}
                {LOCAL_ONLY && " (🔒 LAN)"}
              </div>
            </div>
          </div>

          <div className="right">
            <div
              id="rx-pane"
              className={
                "screen-wrap " + (rxFit === "cover" ? "fit-cover" : "fit-contain")
              }
            >
              <video
                ref={rxVideoRef}
                autoPlay
                playsInline
                controls={false}
                muted={rxMuted}
                className="screen-video"
              />
              {!rxHasStream && (
                <div className="screen-overlay">
                  <div className="screen-placeholder">
                    {LOCAL_ONLY ? <IconRouter size={54} /> : <IconMonitor />}
                    <div className="ph-title">
                      {LOCAL_ONLY
                        ? "🔒 LAN Mode: Waiting for sender"
                        : "Waiting for screen share"}
                    </div>
                    <div className="ph-sub">
                      {LOCAL_ONLY
                        ? "Both devices must be on the same Wi‑Fi/network. Local connections only."
                        : "Share the room code to start receiving."}
                    </div>
                  </div>
                </div>
              )}
              {rxHasStream && rxMuted && (
                <div
                  className="unmute-overlay"
                  onClick={() => {
                    setRxMuted(false);
                    try {
                      const v = rxVideoRef.current;
                      if (v) {
                        v.muted = false;
                        v.volume = 1.0;
                        v.play().catch(() => {});
                      }
                    } catch {}
                  }}
                >
                  Tap to unmute
                </div>
              )}
            </div>
            <div className="right-actions">
              <Button
                className="ghost"
                onClick={() => enterFullscreenOrImmersive("rx-pane")}
              >
                Fullscreen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
